import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, Package } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCategories } from "@/context/CategoriesContext";
import { formatR } from "@/lib/api";

export function ProductCard({ product, index = 0 }) {
  const { add } = useCart();
  const { catName } = useCategories();
  const p = product;
  const discount = p.on_special ? Math.round((1 - p.effective_price / p.price) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.07 }}
      className="group bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:border-brand/30 hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300 overflow-hidden flex flex-col"
      data-testid={`product-card-${p.sku || p.id}`}
    >
      <Link to={`/product/${p.id}`} className="relative block overflow-hidden aspect-[4/3] bg-cream" data-testid={`product-link-${p.sku || p.id}`}>
        {p.image ? (
          <img
            src={p.image}
            alt={p.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-300">
            <Package size={36} strokeWidth={1.2} />
            <span className="text-[10px] font-bold uppercase tracking-widest">Photo coming soon</span>
          </div>
        )}
        {p.on_special && (
          <span className="absolute top-3 left-3 bg-brand text-white text-[11px] font-bold px-2.5 py-1 rounded-full" data-testid={`special-badge-${p.sku || p.id}`}>
            SAVE {discount}%
          </span>
        )}
        {!p.in_stock && (
          <span className="absolute inset-0 bg-navy-dark/55 flex items-center justify-center text-white text-xs font-bold tracking-wider">
            OUT OF STOCK
          </span>
        )}
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-brand mb-1">
          {catName(p.category)}
        </p>
        <Link to={`/product/${p.id}`} className="font-display font-semibold text-navy text-[15px] leading-snug hover:text-brand transition-colors line-clamp-2">
          {p.name}
        </Link>
        <div className="mt-auto pt-3 flex items-end justify-between gap-2">
          <div>
            {p.on_special && (
              <p className="text-xs text-slate-400 line-through font-mono">{formatR(p.price)}</p>
            )}
            <p className="text-lg font-extrabold text-navy font-mono" data-testid={`price-${p.sku || p.id}`}>
              {p.effective_price > 0 ? formatR(p.effective_price) : "Price on request"}
            </p>
          </div>
          {p.variants && p.variants.length > 0 ? (
            <Link
              to={`/product/${p.id}`}
              className="h-10 px-4 rounded-full bg-navy hover:bg-brand text-white text-xs font-bold flex items-center gap-1.5 transition-colors"
              data-testid={`choose-options-${p.sku || p.id}`}
            >
              <ShoppingCart size={14} /> Choose
            </Link>
          ) : (
            <button
              onClick={() => add(p)}
              disabled={!p.in_stock}
              className="h-10 px-4 rounded-full bg-navy hover:bg-brand text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-40 transition-colors"
              data-testid={`add-to-cart-${p.sku || p.id}`}
            >
              <ShoppingCart size={14} /> Add
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
